import { ApplicationDataBaseField } from './ApplicationDataBaseField';
import { Controller } from 'react-hook-form';
import { Button, MenuItem } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import { ApplicationDataFormFieldPath } from '../ApplicationDataForm/ApplicationDataFormSchema';
import { ApplicationDataFieldCommonProps } from './types';
import { useApplicationDataFieldContext } from './useApplicationDataFieldContext';
import { useMemo } from 'react';

function defaultFormatter(value: unknown) {
  return value ? String(value) : '-';
}

export interface ApplicationDataSelectFieldProps
  extends ApplicationDataFieldCommonProps {
  name: ApplicationDataFormFieldPath;
  formatter?: (value: unknown) => string;
  options: Array<{
    value: unknown;
    label?: string;
    isVisible?: ApplicationDataFieldCommonProps['isVisible'];
  }>;
  /**
   * Used to update other fields when this field updates
   */
  onChangeSideEffect?: (value: unknown) => void;
}

export const ApplicationDataSelectField: React.FC<
  ApplicationDataSelectFieldProps
> = ({
  name,
  formatter = defaultFormatter,
  options,
  onChangeSideEffect,
  ...props
}) => {
  const ctx = useApplicationDataFieldContext(props.applicationID);

  const visibleOptions = useMemo(() => {
    const collator = new Intl.Collator(navigator.language);
    return options
      .filter(({ isVisible = true }) =>
        typeof isVisible === 'function' ? isVisible(ctx) : Boolean(isVisible),
      )
      .map((option) => ({
        ...option,
        label:
          option.label !== undefined ? option.label : formatter(option.value),
      }))
      .sort(({ label: labelA }, { label: labelB }) =>
        collator.compare(labelA, labelB),
      );
  }, [ctx, formatter, options]);

  return (
    <Controller
      name={name}
      render={({ field, fieldState }) => {
        const formattedValue =
          options.find(({ value }) => value === field.value)?.label ??
          formatter(field.value as never);

        return (
          <ApplicationDataBaseField
            errorMessage={
              fieldState.invalid ? fieldState.error?.message : undefined
            }
            valueElement={formattedValue}
            inputElement={
              <Select<(typeof options)[number]>
                items={visibleOptions}
                itemRenderer={(option, { ref, index, handleClick }) => (
                  <MenuItem
                    text={option.label}
                    ref={ref}
                    key={index}
                    onClick={handleClick}
                  />
                )}
                filterable={false}
                onItemSelect={(option) => {
                  field.onChange(option.value);
                  if (onChangeSideEffect) onChangeSideEffect(option.value);
                }}
                activeItem={
                  visibleOptions.find(
                    (option) => option.value === field.value,
                  ) ?? null
                }
                popoverProps={{
                  minimal: true,
                  onClose: field.onBlur,
                }}
              >
                <Button
                  alignText="left"
                  text={formattedValue}
                  icon="double-caret-vertical"
                  fill
                />
              </Select>
            }
            {...props}
          />
        );
      }}
    />
  );
};
